import {
  type ColumnDef,
  type ColumnVisibilityState,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Columns3, RotateCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { CopyButton } from "~/components/copy-button";
import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import { EmptyState } from "~/components/empty-state";
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui";
import { useCompoundEntityList } from "~/features/compoundEntities/hooks";
import type { CompoundRow, CompoundSearchCondition } from "~/features/compoundEntities/types";
import { toCompoundRowList } from "~/features/compoundEntities/utils/transformers";

// V1 has no sort UI — compound records are always newest-first (createdAt DESC).
const FIXED_SORTING: SortingState = [];

const COMPOUND_COLUMNS = [
  { id: "id", labelKey: "model.common.id" },
  { id: "key", labelKey: "model.common.key" },
  { id: "name", labelKey: "model.common.name" },
  { id: "recordType", labelKey: "model.common.recordType" },
  { id: "createdAt", labelKey: "model.common.createdAt" },
  { id: "updatedAt", labelKey: "model.common.updatedAt" },
] as const;

interface CompoundEntitiesTableProps {
  conditions: readonly CompoundSearchCondition[];
  version?: string;
  gdtfFrom?: string;
  gdtfTo?: string;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  onSelect: (id: string) => void;
}

const helper = createColumnHelper<DataTableFeatures, CompoundRow>();

export function CompoundEntitiesTable({
  conditions,
  version,
  gdtfFrom,
  gdtfTo,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  onSelect,
}: CompoundEntitiesTableProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { data, refetch } = useCompoundEntityList({ conditions, version, gdtfFrom, gdtfTo, pageIndex, pageSize });
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});

  const rows = useMemo(
    () => toCompoundRowList(data.compoundCompoundRecords, locale),
    [data.compoundCompoundRecords, locale],
  );
  const totalCount = data.compoundCompoundRecordsAggregate.aggregate?.totalCount ?? 0;

  const isVisible = (id: string) => columnVisibility[id] !== false;
  const setVisibility = (id: string, next: boolean) => setColumnVisibility((prev) => ({ ...prev, [id]: next }));
  const toggleAll = (next: boolean) =>
    setColumnVisibility(Object.fromEntries(COMPOUND_COLUMNS.map((c) => [c.id, next])));

  const columns: ColumnDef<DataTableFeatures, CompoundRow, any>[] = [
    helper.accessor("id", {
      id: "id",
      header: () => t("model.common.id"),
      enableSorting: false,
      cell: (info) => (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums text-fg-2">
          {info.getValue()}
          <CopyButton value={info.getValue()} label={t("ui.copyId")} successMessageKey="feature.common.idCopied" />
        </span>
      ),
    }),
    helper.accessor("key", {
      id: "key",
      header: () => t("model.common.key"),
      enableSorting: false,
      cell: (info) => <span className="block w-[20rem] break-all text-[12px] text-fg-2">{info.getValue() || "—"}</span>,
    }),
    helper.accessor("name", {
      id: "name",
      header: () => t("model.common.name"),
      enableSorting: false,
      cell: (info) => (
        <span className="block w-[16rem] whitespace-normal break-words font-medium text-fg-1">
          {info.getValue() || "—"}
        </span>
      ),
    }),
    helper.accessor("recordType", {
      id: "recordType",
      header: () => t("model.common.recordType"),
      enableSorting: false,
      cell: (info) => <span className="whitespace-nowrap text-fg-2">{info.getValue() || "—"}</span>,
    }),
    helper.accessor("createdAt", {
      id: "createdAt",
      header: () => t("model.common.createdAt"),
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="whitespace-nowrap tabular-nums text-fg-2">
            {value ? t("date.long", { date: parseISO(value) }) : "—"}
          </span>
        );
      },
    }),
    helper.accessor("updatedAt", {
      id: "updatedAt",
      header: () => t("model.common.updatedAt"),
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="whitespace-nowrap tabular-nums text-fg-3">
            {value ? t("date.long", { date: parseISO(value) }) : "—"}
          </span>
        );
      },
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      cell: (info) => (
        <div className="flex items-center justify-end">
          <Button type="button" variant="outline" size="sm" onClick={() => onSelect(info.row.original.id)}>
            {t("ui.view")}
          </Button>
        </div>
      ),
    }),
  ];

  const isEmpty = rows.length === 0 && !isPending;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" aria-label={t("ui.columns")}>
              <Columns3 />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuItem onSelect={() => toggleAll(false)}>{t("ui.hideAll")}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleAll(true)}>{t("ui.showAll")}</DropdownMenuItem>
            <DropdownMenuSeparator />
            {COMPOUND_COLUMNS.map((col) => (
              <DropdownMenuCheckboxItem
                key={col.id}
                checked={isVisible(col.id)}
                onCheckedChange={(checked) => setVisibility(col.id, checked === true)}
                onSelect={(e) => e.preventDefault()}
              >
                {t(col.labelKey)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button type="button" variant="outline" size="icon" onClick={() => void refetch()} aria-label={t("ui.reload")}>
          <RotateCw />
        </Button>
      </div>

      {isEmpty ? (
        <EmptyState
          title={t("feature.compoundEntities.empty.title")}
          description={t("feature.compoundEntities.empty.description")}
        />
      ) : (
        <RemoteDataTable<CompoundRow>
          columns={columns}
          data={rows}
          rowCount={totalCount}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          sorting={FIXED_SORTING}
          onSortingChange={() => {}}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          isPending={isPending}
          emptyMessage={t("feature.common.noData")}
        />
      )}
    </div>
  );
}
